import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Tab, Tabs, makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import React from 'react'

export interface TabItem {
    label: string
    content?: React.ReactNode
    disabled?: boolean
}

const useEditorTabsStyles = makeStyles((theme) => ({
    container: {
        paddingTop: pxToRem(16),
        paddingLeft: pxToRem(7),
        justifyContent: 'flex-end',
        display: 'flex',
        '& button:last-child': {
            marginLeft: theme.spacing(1),
        },
    },
    tabs: {
        flexWrap: 'wrap',
        minHeight: 35,
        justifyContent: 'space-between',
        '& .MuiTabs-indicator': {
            backgroundColor: '#185E8E',
            height: pxToRem(4),
        },
        '& .MuiTab-root': {
            fontSize: pxToRem(16),
            paddingBottom: 0,
            color: '#314959',
            minHeight: 35,
            padding: theme.spacing(0, 1.5),
        },
        '& .activeTab': {
            color: '#185E8E',
        },
    },
}))

export const EditorTabs = React.memo<{
    tabs: TabItem[]
    value: number
    children?: React.ReactNode
    onChange: (index: number) => void
    variant?: 'standard' | 'fullWidth' | 'scrollable'
    scrollButtons?: 'auto' | 'on' | 'off'
    orientation?: 'horizontal' | 'vertical'
    className?: string
    tabClassName?: string
    activeTabClassName?: string
}>(
    ({
        tabs,
        value,
        children,
        onChange,
        variant = 'fullWidth',
        scrollButtons = 'off',
        orientation = 'horizontal',
        className,
        tabClassName,
        activeTabClassName = 'activeTab',
    }) => {
        const classes = useEditorTabsStyles()

        const activeTabIndex = React.useMemo(() => value - 1, [value])

        const handleTabsOnChange = React.useCallback(
            (_, newValue) => onChange(newValue),
            [onChange]
        )

        const renderTabs = React.useMemo(() => {
            return tabs.map((tab, index) => (
                <Tab
                    role="tab"
                    key={`${tab.label}-${index}`}
                    label={tab.label}
                    disabled={tab.disabled}
                    className={clsx(
                        tabClassName,
                        activeTabIndex === index && activeTabClassName
                    )}
                />
            ))
        }, [tabs, activeTabIndex, tabClassName, activeTabClassName])

        return (
            <GridContainer
                className={`${classes.container} ${className || ''}`}
                direction="row"
            >
                <GridItem item md>
                    <Tabs
                        value={activeTabIndex}
                        onChange={handleTabsOnChange}
                        data-testid="editor-tabs"
                        aria-label="editor-tabs"
                        variant={variant}
                        scrollButtons={scrollButtons}
                        orientation={orientation}
                        selectionFollowsFocus
                        className={clsx(classes.tabs, className)}
                    >
                        {renderTabs}
                    </Tabs>
                </GridItem>

                {tabs[activeTabIndex]?.content}
                {children}
            </GridContainer>
        )
    }
)
